import _ from 'lodash';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {connect} from 'react-redux';
import {Icon, Tooltip, DataGrid} from '@perion-undertone/ut-react-common';
import {AutoSizer} from 'react-virtualized';
import GridNoRowsTemplate from '../../GridNoRowsTemplate';
import {supplySourcesGridSchema, settings} from './grid/supplySourcesGridSchema';
import {clearAllGridFilters, PerionLoader} from './actionImports';
import BulkActionsOverlay from '../../grid/BulkActionsOverlay';
import {appStates} from "../../../constants";
import {SelectColumn, adaptColumnsForV7} from '../../../utils/reactDataGridV7';

const CLASS_NAMES = {
    SUPPLY_BODY_CONTAINER: 'supply-body-container',
    SUPPLY_BODY_INNER_CONTAINER: 'supply-body-inner-container',
    SUPPLY_BODY_INNER_LOADING_CONTAINER: 'supply-body-inner-container--is-loading',
    SUPPLY_GRID_SIZER: 'supply-grid-sizer',
    FILTER_INFO_TOOLTIP: 'filter-info-tooltip',
    FILTERS_TOOLTIP_TITLE: 'filter-tooltip-title',
    FILTERS_TOOLTIP_LIST: 'filter-tooltip-list',
    FILTERED_BY: 'filtered-by',
    CLEAR_FILTER_ICON_CONTAINER: 'clear-all-filters-container',
    CLEAR_FILTER_ICON: 'clear-all-filters-icon',
    CLEAR_FILTER_BUTTON: 'clear-all-filters-button'
};

const rowKeyGetter = (row) => row && row.publishersSupplySourcesId;

class SupplySourcesGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // v7 selection model: a Set of row keys (publishersSupplySourcesId).
            selectedRowIds: new Set()
        };
    }

    componentDidMount() {
        this.getSupplySourcesGridSchema();
    }

    componentDidUpdate(prevProps) {
        const prevSupplySources = prevProps.supplySources && prevProps.supplySources.data;
        const currSupplySources = this.props.supplySources && this.props.supplySources.data;

        if (prevSupplySources && currSupplySources && !_.isEqual(prevSupplySources, currSupplySources)) {
            // Drop selection entries that no longer exist in the refreshed data.
            const validIds = new Set(currSupplySources.map(s => s.publishersSupplySourcesId));
            const {selectedRowIds} = this.state;
            let changed = false;
            const next = new Set();
            selectedRowIds.forEach(id => {
                if (validIds.has(id)) {
                    next.add(id);
                } else {
                    changed = true;
                }
            });
            if (changed) {
                this.setState({selectedRowIds: next});
            }
        }
    }

    handleClearAllFilters = () => {
        this.props.dispatch(clearAllGridFilters());
    };

    renderFilteredByTrigger = (numOfFilters) => {
        return (<span className={CLASS_NAMES.FILTERED_BY}>Filter by {numOfFilters}</span>);
    };

    renderFilterInfo = (customization, numOfFilters) => {
        return (
            <div>
                <Tooltip placement="bottom-start"
                         trigger={this.renderFilteredByTrigger(numOfFilters)}
                         className={'placements-filter-tooltip'}
                         hookId="supply-sources-filter-tooltip"
                >
                    <span className={CLASS_NAMES.FILTERS_TOOLTIP_TITLE}>
                        Used Filters ({numOfFilters})
                    </span>
                    <ul className={CLASS_NAMES.FILTERS_TOOLTIP_LIST}>
                        {customization.filters.map(filter => {
                            return <li key={filter.field}>{filter.field} - {this.getActiveValues(filter)}</li>
                        })}
                    </ul>
                </Tooltip>
                <span className={CLASS_NAMES.CLEAR_FILTER_ICON_CONTAINER}>
                    <div className={CLASS_NAMES.CLEAR_FILTER_ICON}>
                        <Icon icon="icon-filter_clear" hookId="supply-sources-filter-clear" />
                    </div>
                    <div className={CLASS_NAMES.CLEAR_FILTER_BUTTON}>
                        <Icon icon="icon-close_filter_clear"
                              onClick={this.handleClearAllFilters}
                              hookId="supply-sources-filter-button"
                        />
                    </div>
                </span>
            </div>
        );
    }

    getActiveValues(filter) {
        if (filter.field !== 'status' && filter.field !== 'store') {
            return filter.value;
        }
        const {allFilterOptions} = this.props.customization;
        const filterField = filter.field === 'store' ? 'supplySourceType' : filter.field === 'status' ? 'supplySourceStatus' : filter.field;
        const filterOptions = allFilterOptions[filterField];
        const values = filter.value;
        const foundFilters = filterOptions && filterOptions.filter(option => !values.includes(option.alias));
        const filterToShow = foundFilters && foundFilters.map(filter => filter.name);

        return filterToShow[0] ? filterToShow.join(', ') : 'none';
    }

    getSupplySourcesGridSchema = () => {
        const {features} = this.props;

        const settingsFeature = features.find(feature => feature.featureName === 'SupplySourceSettings');
        const isSettingsVisible = supplySourcesGridSchema.find(field => field.key === "supply-actions");
        settingsFeature.isEnabled && !isSettingsVisible && supplySourcesGridSchema.push(settings);
    }

    cancelBulkDelete = () => {
        this.setState({selectedRowIds: new Set()});
    }

    onSelectedRowsChange = (selectedRowIds) => {
        this.setState({selectedRowIds});
    }

    getSelectedRows = () => {
        const {selectedRowIds} = this.state;
        const data = (this.props.supplySources && this.props.supplySources.data) || [];
        return data.filter(row => selectedRowIds.has(row.publishersSupplySourcesId));
    }

    render() {
        const {supplySources, customization} = this.props;
        const rowSource = (supplySources && supplySources.data) || [];
        const numOfFilters = customization && customization.filters ? customization.filters.length : 0;
        const selectedRows = this.getSelectedRows();
        const selectedGroupIds = selectedRows.map(r => r.publishersSupplySourcesId);
        const selectedGroupDisplayNames = selectedRows.map(r => r.displayName);
        const innerContainerClassNames = cx(CLASS_NAMES.SUPPLY_BODY_INNER_CONTAINER, {
            [CLASS_NAMES.SUPPLY_BODY_INNER_LOADING_CONTAINER]: supplySources && supplySources.state === appStates.LOADING
        });
        const isLoading = _.isNil(supplySources && supplySources.data) || (supplySources && supplySources.state === appStates.LOADING);

        // The v6 grid sized the selection column to 60px via `rowSelection`'s
        // implicit checkbox column width. v7's `SelectColumn` is a plain
        // column object that defaults to ~35px; spread + override to keep
        // the previous layout (also pin `minWidth` so v7's auto-sizing can't
        // shrink it below that).
        const columns = [
            {...SelectColumn, width: 60, minWidth: 60},
            ...adaptColumnsForV7(supplySourcesGridSchema)
        ];

        return (<div className={CLASS_NAMES.SUPPLY_BODY_CONTAINER}>
                {isLoading && <PerionLoader />}
                {!_.isNil(supplySources && supplySources.data) && <div className={innerContainerClassNames}>
                                <div className={CLASS_NAMES.FILTER_INFO_TOOLTIP}>
                                    {numOfFilters > 0 ? this.renderFilterInfo(customization, numOfFilters) : null}
                                </div>
                                <div className={CLASS_NAMES.SUPPLY_GRID_SIZER}>
                                    <AutoSizer>
                                        {({height, width}) => (
                                            <DataGrid columns={columns}
                                                      rows={rowSource}
                                                      rowKeyGetter={rowKeyGetter}
                                                      selectedRows={this.state.selectedRowIds}
                                                      onSelectedRowsChange={this.onSelectedRowsChange}
                                                      rowHeight={56}
                                                      headerRowHeight={41}
                                                      defaultHeight={height}
                                                      defaultWidth={width}
                                                      emptyRowsView={GridNoRowsTemplate}
                                            />
                                        )}
                                    </AutoSizer>
                                </div>
                            </div>}
                    {selectedGroupIds.length > 0
                        && (<BulkActionsOverlay cancelBulkDelete={this.cancelBulkDelete}
                                                selectedGroupIds={selectedGroupIds}
                                                selectedGroupDisplayNames={selectedGroupDisplayNames}
                                                displayBlockOption={true}
                                                displayDeleteOption={false}
                    />)}
            </div>
        );
    }
}

SupplySourcesGrid.propTypes = {
    supplySources: PropTypes.object,
    customization: PropTypes.object,
    features: PropTypes.array,
    dispatch: PropTypes.func
};

const mapStateToProps = ({supply, grid, features}) => ({
    supplySources: supply.supplySources,
    customization: grid.customization,
    features
});

export default connect(mapStateToProps)(SupplySourcesGrid);
